/** @jsx jsx */

import type { CSSProperties, ReactNode } from 'react';
import { memo, useMemo } from 'react';
import type { CSSObject, DataAttributeMap } from '@balance-web/core';
import { buildDataAttributes, jsx } from '@balance-web/core';
import {
  TABLE_CELL_BORDER_DISABLED,
  TABLE_ROW_TINTS,
  getBoxBackgrounds,
  useTheme,
} from '@balance-web/theme';
import { isDefined } from '@balance-web/utils';

import { useTableContext } from '../tableContext';
import { siftAriaAttributes } from '../utils';
import type { CellAlign } from '../types';
import { useTableRowContext } from '../rowContext';

import { CellContent } from './CellContent';

// Main
// ------------------------------

export type TableCellProps = UseCellStylesProps & {
  /** The contents of the cell. */
  children: ReactNode;
  /** Optionally provide an ID. */
  id?: string;
  /**
   * The role of the cell.
   * @default 'gridcell'
   */
  role?: 'gridcell' | 'rowheader' | 'columnheader';
  /**
   * When only a subset of columns are present, use the aria-colindex attribute
   * to let assistive technologies know what portions of the content are being
   * displayed, and that all the table's content is not present.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-colindex
   */
  'aria-colindex'?: number;
  /**
   * The aria-colspan attribute defines the number of columns spanned by an
   * individual cell. The value should be a positive integer.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-colspan
   */
  'aria-colspan'?: number;
  /**
   * The aria-rowspan attribute defines the number of rows spanned by an
   * individual cell. The value should be a positive integer.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-rowspan
   */
  'aria-rowspan'?: number;
};

function UnmemoizedTableCell({
  children,
  id,
  role = 'gridcell',
  showDivider,
  flex,
  maxWidth,
  minWidth,
  stickyEdge,
  stickyOffset,
  ...props
}: TableCellProps) {
  const { dataAttributes, css, style } = useCellStyles({
    ...props,
    flex,
    maxWidth,
    minWidth,
    showDivider,
    stickyEdge,
    stickyOffset,
  });
  const ariaAttributes = siftAriaAttributes(props);

  return (
    <div
      css={css}
      id={id}
      role={role}
      style={style}
      {...ariaAttributes}
      {...dataAttributes}
      {...props}
    >
      <CellContent>{children}</CellContent>
    </div>
  );
}

// NOTE: only effective when `children` is a string or number.
// Consumers must memoize non-primitive children for best performance.
export const TableCell = memo(UnmemoizedTableCell);

// Styles
// ------------------------------

export type UseCellStylesProps = {
  /**
   * Sets the horizontal alignment of cell contents.
   * @default 'left'
   */
  align?: CellAlign;
  /**
   * Sets the vertical alignment for content within each cell.
   * @default 'center'
   */
  alignY?: 'top' | 'bottom' | 'center';
  /**
   * Shares spare width between cells in proportion to this number - seed it
   * with the column's intended pixel width. Takes precedence over `width`, as
   * AG Grid's `flex` does.
   */
  flex?: number;
  /** Sets the maximum width of the cell. */
  maxWidth?: number | string;
  /** Sets the minimum width of the cell. */
  minWidth?: number | string;
  /** Sets the width of the cell. */
  width?: number | string;
  /**
   * Applies "sticky" positioning to the cell, offset relative to its nearest
   * scrolling ancestor.
   *
   * The edge defaults to the sign of the value — negative offsets stick to the
   * right — but prefer `stickyEdge` to say so outright. `-0` cannot express a
   * right edge: build pipelines constant-fold it to `0`, silently turning the
   * cell back into a left-sticky one.
   */
  stickyOffset?: number;
  /**
   * Which edge the cell sticks to. Defaults to the sign of `stickyOffset`.
   */
  stickyEdge?: 'left' | 'right';
  /**
   * Whether the cell should render a divider between it and the next cell.
   * @default stickyOffset !== undefined
   */
  showDivider?: boolean;
  /**
   * Sets the inline [style](https://reactjs.org/docs/dom-elements.html#style) for
   * the element. Only use as a **last resort**.
   */
  UNSAFE_style?: CSSProperties;
};

type UseCellStylesResult = {
  css: CSSObject[];
  dataAttributes: DataAttributeMap;
  style: CSSProperties;
};

// TODO: review memoization perf. needs to be measured
export function useCellStyles(props: UseCellStylesProps): UseCellStylesResult {
  const { borderWidth, semantic, spacing } = useTheme();
  const tableContext = useTableContext();
  const rowContext = useTableRowContext();

  const alignY = props.alignY || rowContext.alignY || tableContext.alignY;

  const {
    align = 'start',
    flex,
    maxWidth,
    minWidth,
    width,
    showDivider: showDividerPreference,
    stickyEdge: stickyEdgePreference,
    stickyOffset,
    UNSAFE_style,
  } = props;
  const isFlexible = isDefined(flex);
  const isSticky = isDefined(stickyOffset);
  const showDivider = showDividerPreference ?? isSticky;
  const stickyEdge =
    stickyEdgePreference ?? (isNegative(stickyOffset) ? 'right' : 'left');

  // dynamic properties: would drastically fork class names so we pass straight
  // through to the style prop
  const style = useMemo(() => {
    let baseStyles = {
      ...UNSAFE_style,
      flexGrow: flex,
      maxWidth,
      minWidth,
      width: isFlexible ? undefined : width,
    };

    if (isSticky && typeof stickyOffset === 'number') {
      return { ...baseStyles, [stickyEdge]: Math.abs(stickyOffset) };
    }

    return baseStyles;
  }, [
    flex,
    isFlexible,
    isSticky,
    stickyEdge,
    stickyOffset,
    maxWidth,
    minWidth,
    width,
    UNSAFE_style,
  ]);

  // static-ish properties: use data attributes to target prop-specific styles,
  // which limits the number of class names generated by emotion
  const sticky = isSticky ? stickyEdge : undefined;
  const dataAttributes = useMemo(() =>
    // reduce noise in the markup by omitting the default values
    {
      return buildDataAttributes({
        align: align === 'left' ? undefined : align,
        'align-y': alignY === 'center' ? undefined : alignY,
        density:
          tableContext.density === 'regular' ? undefined : tableContext.density,
        layout: width && !isFlexible ? 'fixed' : undefined,
        'show-divider': showDivider,
        sticky,
      });
    }, [
    align,
    alignY,
    isFlexible,
    tableContext.density,
    showDivider,
    sticky,
    width,
  ]);

  const defaultBorder = `${borderWidth.standard} solid ${semantic.colour.surface.neutral.border}`;

  const css = useMemo(() => {
    return [
      {
        backgroundColor: semantic.colour.surface.neutral.background,
        borderBottom: defaultBorder,
        boxSizing: 'border-box' as const,
        display: 'flex',
        minWidth: 0, // resolve flex issues with text trucation
        paddingInline: spacing.medium,

        // defaults
        alignItems: 'center', // alignY
        justifyContent: 'flex-start', // align
        paddingBlock: spacing.medium, // density
        // layout
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 0,
      },

      // GROUP
      {
        '[data-rowgroup-type="head"] &': {
          userSelect: 'none' as const,
        },
        // Hide border bottom on last row of group
        '[data-rowgroup-type="body"] [role="row"]:last-of-type &, [data-rowgroup-type="foot"] &': {
          borderBottom: 0,
        },
        /**
         *
         * Above css hides border on all rows when dragging is enabled because each row is wrapped in a drag wrapper which makes it :last-of-type.
         * The next 2 selectors undo above css and reapplies it targetting the drag wrapper as :last-of-type.
         * */
        '[data-rowgroup-type="body"] [data-rbd-draggable-id] [role="row"] &, [data-rowgroup-type="foot"] &': {
          borderBottom: defaultBorder,
        },
        '[data-rowgroup-type="body"] [data-rbd-draggable-id] [role="row"][data-row-disabled="true"] &': {
          borderBottomColor: TABLE_CELL_BORDER_DISABLED,
        },
        '[data-rowgroup-type="body"] [data-rbd-draggable-id]:last-of-type [role="row"] &, [data-rowgroup-type="foot"] &': {
          borderBottom: 0,
        },
        '[data-rowgroup-type="foot"] &': {
          borderTop: `${borderWidth.standard} solid ${semantic.colour.surface.neutral.border}`,
        },
      },

      // ROW

      // tone
      {
        '[data-row-tone="cautious"] &': {
          backgroundColor: TABLE_ROW_TINTS.cautious,
        },
        '[data-row-tone="critical"] &': {
          backgroundColor: TABLE_ROW_TINTS.critical,
        },
        '[data-row-tone="positive"] &': {
          backgroundColor: TABLE_ROW_TINTS.positive,
        },
        '[data-row-disabled="true"] &': {
          backgroundColor: TABLE_ROW_TINTS.disabled,
          borderBottomColor: TABLE_CELL_BORDER_DISABLED,
          opacity: 0.25,
        },
      },
      // interaction
      {
        '&[data-interaction]': {
          flex: '0 0 auto',
          position: 'relative' as const,
          width: 40,
        },
      },
      // interaction: selection
      {
        '[role="row"][aria-selected="true"] &': {
          backgroundColor: getBoxBackgrounds(semantic.colour)
            .selectableSelected,
        },
      },
      // interaction: drag
      {
        '&[data-interaction="drag"]': {
          color: semantic.colour.text.textTertiary,

          ':hover, :focus': {
            color: semantic.colour.text.textPrimary,
          },
        },
      },

      // CELL

      // sorting
      {
        '[role="columnheader"][tabindex]&': {
          cursor: 'pointer',
        },
      },

      // sticky cells display a divider
      {
        '&[data-sticky]': {
          position: 'sticky' as const,
        },
        '&:not([data-sticky="right"])[data-show-divider="true"]': {
          borderRightColor: semantic.colour.surface.neutral.border,
          borderRightStyle: 'solid' as const,
          borderRightWidth: borderWidth.standard,
        },
        '&[data-sticky="right"][data-show-divider="true"]': {
          borderLeftColor: semantic.colour.surface.neutral.border,
          borderLeftStyle: 'solid' as const,
          borderLeftWidth: borderWidth.standard,
        },
      },
      // layout rules
      {
        // '&[data-layout="fluid"]': { flex: '1 1 0%' },
        '&[data-layout="fixed"]': { flex: '0 0 auto' },
      },
      // align rules
      {
        // '&[data-align="left"]': { justifyContent: 'flex-start' },
        '&[data-align="center"]': { justifyContent: 'center' },
        '&[data-align="right"]': { justifyContent: 'flex-end' },
      },
      {
        '&[data-align-y="top"]': { alignItems: 'flex-start' },
        // '&[data-align-y="center"]': { alignItems: 'center' },
        '&[data-align-y="bottom"]': { alignItems: 'flex-end' },
      },
      // density rules
      {
        '&[data-density="compact"]': { paddingBlock: spacing.small },
        // '&[data-density="regular"]': { paddingBlock: spacing.medium },
        '&[data-density="spacious"]': { paddingBlock: spacing.large },
      },
    ];
  }, [borderWidth, semantic, spacing]);

  return { dataAttributes, css, style };
}

function isNegative(value?: number) {
  if (typeof value === 'undefined') {
    return false;
  }

  const isNegativeZero = 1 / value === -Infinity;
  return isNegativeZero || value < 0;
}
